/**
 * @voltxampere/web — English bundle (Sprint 11).
 *
 * Mirror of `tr.ts`. Same key set; missing keys would surface
 * the raw key in the UI, which is a useful smoke signal during
 * development.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { TranslationKey } from "./tr.js";

export const en: Readonly<Record<TranslationKey, string>> = {
  // App
  "app.title": "Volt × Ampere",
  "app.tagline": "Electric & electronic studio",

  // Topbar
  "topbar.untitled": "Untitled Circuit",
  "topbar.user.menu": "Account",
  "topbar.locale.toggle": "TR",

  // Sprint 18 Paket B — nav-tabs
  "nav.tab.landing": "Welcome",
  "nav.tab.simulator": "Simulator",
  "nav.tab.presets": "Circuits",
  "nav.tab.inspector": "Inspector",
  "nav.tab.settings": "Settings",
  "shortcut.pane.landing": "Switch to welcome",
  "shortcut.pane.simulator": "Switch to simulator",
  "shortcut.pane.presets": "Switch to circuits",
  "shortcut.pane.inspector": "Switch to inspector",
  "shortcut.pane.settings": "Switch to settings",

  // Sprint 18 Paket B B7 — Settings page
  "settings.nav.label": "Settings",
  "settings.tab.appearance": "Appearance",
  "settings.tab.language": "Language & Region",
  "settings.tab.simulation": "Simulation",
  "settings.tab.shortcuts": "Keyboard Shortcuts",
  "settings.tab.a11y": "Accessibility",
  "settings.tab.about": "About",
  "settings.appearance.title": "Appearance",
  "settings.appearance.sub": "Theme character, canvas background, symbol standard, wire curve.",
  "settings.appearance.theme.label": "Theme",
  "settings.appearance.theme.row.title": "Primary palette",
  "settings.appearance.theme.row.desc": "Zenit default · night observatory + brass accent",
  "settings.appearance.brightness.row.title": "Brightness",
  "settings.appearance.brightness.row.desc": "Dimmer on OLED, brighter on retina screens",
  "settings.appearance.canvas.label": "Canvas",
  "settings.appearance.canvas.background.title": "Background style",
  "settings.appearance.canvas.background.desc": "Grid character",
  "settings.appearance.canvas.wire.title": "Wire curve",
  "settings.appearance.canvas.wire.desc": "How wires take their path",
  "settings.appearance.canvas.symbol.title": "Symbol standard",
  "settings.appearance.canvas.symbol.desc": "IEC zigzag (Europe) · ANSI rectangle (USA)",
  "settings.appearance.canvas.fx.title": "Current flow animation",
  "settings.appearance.canvas.fx.desc":
    "Show current flow on wires with brass-hot particles during transient simulation. Auto-disables when the system 'reduced-motion' preference is set.",
  "settings.appearance.canvas.fx.option.on": "On",
  "settings.appearance.canvas.fx.option.off": "Off",
  "settings.appearance.canvas.heatmap.title": "Voltage heatmap",
  "settings.appearance.canvas.heatmap.desc":
    "Show node potentials with colored halo rings. High = brass-hot, mid = slate-blue, low = deep-blue. Static overlay — reduced-motion does not affect it.",
  "settings.appearance.canvas.heatmap.option.on": "On",
  "settings.appearance.canvas.heatmap.option.off": "Off",
  "settings.appearance.canvas.gradient.title": "Voltage gradient",
  "settings.appearance.canvas.gradient.desc":
    "Color each wire by its net voltage (HSL space lerp). Independent of the heatmap toggle; static overlay.",
  "settings.appearance.canvas.gradient.option.on": "On",
  "settings.appearance.canvas.gradient.option.off": "Off",
  // Sprint 31 — audio settings (Speaker + Buzzer master controls).
  "settings.audio.title": "Audio",
  "settings.audio.masterVolume": "Master volume",
  "settings.audio.masterVolume.desc":
    "Master volume for speaker + buzzer output (0..100). Zero = silent (same effect as the Mute toggle below; AYARLAR persists this value across sessions).",
  "settings.audio.muted": "Mute",
  "settings.audio.muted.desc":
    "Silence all audio output. Equivalent to setting master volume to 0; this toggle is the persistent preference.",
  "settings.audio.muted.option.on": "Muted",
  "settings.audio.muted.option.off": "On",
  "settings.audio.autoplay.hint":
    "Browser policies require a click before audio plays. Audio starts when you press Run / F5 / drag the volume slider. Auto-disabled when the system 'reduce-motion' preference is set.",
  "settings.audio.autoplay.dismiss": "Got it",
  // Sprint 37-4 — Damage Sounds (8 damage sound effects toggle).
  "settings.damageAudio.title": "Damage Sounds",
  "settings.damageAudio.sub":
    "Plays 8 sound effects when a component pops, capacitor swells or resistor burns (LED pop, capacitor bang, sizzle, crackle, hiss, zap, creak, cooldown).",
  "settings.damageAudio.enabled": "Damage sound effects",
  "settings.damageAudio.enabled.desc":
    "8 Tone.js sound effects in sync with explosion animations. Master volume + system reduce-motion preference also apply (6-layer audio guard).",
  "settings.damageAudio.enabled.option.on": "On",
  "settings.damageAudio.enabled.option.off": "Off",
  // Sprint 32 — time scrubber + bookmark UI.
  "scrubber.label": "Time scrubber",
  "scrubber.live": "Live",
  "bookmark.add": "Add pin",
  "bookmark.placeholder": "What happened here?",
  "bookmark.delete": "Delete",
  "bookmark.close": "Close",
  "bookmark.maxLength": "{count}/280 characters",
  // Sprint 33-4 — Live Mode (always-live + idle detection) toggles.
  "settings.liveMode.title": "Live Mode",
  "settings.liveMode.sub":
    "Auto-rerun when the circuit changes; reduce frame rate when the app is idle.",
  "settings.liveMode.alwaysLive": "Always live",
  "settings.liveMode.alwaysLive.desc":
    "Auto-rerun simulation when circuit changes. Run button becomes optional.",
  "settings.liveMode.alwaysLive.option.on": "On",
  "settings.liveMode.alwaysLive.option.off": "Off",
  "settings.liveMode.idleDetection": "Power saving",
  "settings.liveMode.idleDetection.desc":
    "Reduces frame rate after 3 minutes of inactivity (60fps → 10fps). Saves CPU and battery.",
  "settings.liveMode.idleDetection.option.on": "On",
  "settings.liveMode.idleDetection.option.off": "Off",
  // Sprint 34-5 — Thermal visualization toggle.
  "settings.thermal.title": "Thermal",
  "settings.thermal.sub":
    "Show component temperature as hot-spot halos on canvas. High power dissipation = red, low = orange.",
  "settings.thermal.visualization": "Thermal visualization",
  "settings.thermal.visualization.desc":
    "Draws live temperature halos around all components on canvas. Inspector's Temperature row is always visible.",
  "settings.thermal.visualization.option.on": "On",
  "settings.thermal.visualization.option.off": "Off",
  // Sprint 35-5 — Explosion Effects toggles (master + smoke separate).
  "settings.explosion.title": "Explosion Effects",
  "settings.explosion.sub":
    "Visual effects when components overheat: LED pop, capacitor swelling, resistor burning. Auto-disabled when system 'reduce-motion' preference is set.",
  "settings.explosion.enabled": "Explosion animations",
  "settings.explosion.enabled.desc":
    "Plays LED 5-stage explosion, capacitor swelling, resistor burning animations. Triggered at severity ≥ 1.2.",
  "settings.explosion.enabled.option.on": "On",
  "settings.explosion.enabled.option.off": "Off",
  "settings.explosion.smokeEnabled": "Smoke particles",
  "settings.explosion.smokeEnabled.desc":
    "Smoke rises from burning resistors and capacitor explosions. Can be disabled separately for performance (200 particle cap).",
  "settings.explosion.smokeEnabled.option.on": "On",
  "settings.explosion.smokeEnabled.option.off": "Off",
  // Sprint 36-5 — Damage (persistent damage + wire overload) toggles.
  "settings.damage.title": "Damage",
  "settings.damage.sub":
    "Exploded components stay permanently damaged and drop out of the circuit. Wires melt and break when current exceeds limits. 'Reset Circuit' restores everything.",
  "settings.damage.persistAcrossReload": "Persist damage across reload",
  "settings.damage.persistAcrossReload.desc":
    "Damaged component state is stored in IDB. Disable to start fresh on every reload.",
  "settings.damage.persistAcrossReload.option.on": "On",
  "settings.damage.persistAcrossReload.option.off": "Off",
  "settings.damage.wireOverloadEnabled": "Wire overload detection",
  "settings.damage.wireOverloadEnabled.desc":
    "Wires gradient yellow → red → black and break when current exceeds limits (default 5A insulation, 10A copper-melt). Disable to keep wires always intact.",
  "settings.damage.wireOverloadEnabled.option.on": "On",
  "settings.damage.wireOverloadEnabled.option.off": "Off",
  "canvas.restart.label": "Reset Circuit",
  "canvas.restart.aria": "Reset damaged components and re-run the circuit",
  "inspector.damaged": "DAMAGED",
  "settings.appearance.deferred":
    "Canvas backend wiring lands in Sprint 19; B7 shows the visual preview only.",
  "settings.language.title": "Language & Region",
  "settings.language.sub": "Interface language. Scientific terms keep their originals.",
  "settings.language.locale.label": "Language",
  "settings.language.locale.row.title": "Interface language",
  "settings.language.locale.row.desc": "TR (default) · EN secondary",
  "settings.simulation.title": "Simulation",
  "settings.simulation.sub":
    "AC sweep, transient analysis and temperature defaults — applied to new circuits.",
  "settings.simulation.tolerances.label": "Tolerances",
  "settings.simulation.reltol.title": "Relative tolerance",
  "settings.simulation.reltol.desc": "reltol for DC solve (fixed, ngspice default)",
  "settings.simulation.abstol.title": "Absolute tolerance",
  "settings.simulation.abstol.desc": "abstol for voltage threshold (fixed, ngspice default)",
  "settings.simulation.maxIter.title": "Max iterations",
  "settings.simulation.maxIter.desc": "Newton loop safety ceiling (fixed)",
  // Sprint 24 A3 — AC defaults form
  "settings.simulation.ac.label": "AC sweep defaults",
  "settings.simulation.ac.fStart.title": "Start frequency",
  "settings.simulation.ac.fStart.desc": "In hertz — minimum 1 mHz",
  "settings.simulation.ac.fStop.title": "Stop frequency",
  "settings.simulation.ac.fStop.desc": "In hertz — maximum 1 GHz",
  "settings.simulation.ac.points.title": "Point count",
  "settings.simulation.ac.points.desc": "Total for linear, per decade/octave for dec/oct",
  "settings.simulation.ac.sweep.title": "Sweep type",
  "settings.simulation.ac.sweep.desc": "Linear · octave · decade · log-linear",
  "settings.simulation.ac.sweep.lin": "Linear",
  "settings.simulation.ac.sweep.oct": "Octave",
  "settings.simulation.ac.sweep.dec": "Decade",
  "settings.simulation.ac.sweep.log": "Log",
  // Sprint 24 A3 — Transient defaults form
  "settings.simulation.tran.label": "Transient defaults",
  "settings.simulation.tran.tStop.title": "Stop time",
  "settings.simulation.tran.tStop.desc": "In seconds — when the analysis halts",
  "settings.simulation.tran.maxStep.title": "Max step size",
  "settings.simulation.tran.maxStep.desc": "In seconds — adaptive controller upper bound",
  // Sprint 24 A3 — Temperature
  "settings.simulation.temp.label": "Temperature",
  "settings.simulation.temp.row.title": "Ambient temperature",
  "settings.simulation.temp.row.desc": "In °C — semiconductor models use this value",
  // Sprint 24 A3 — Reset
  "settings.simulation.reset.title": "Reset to defaults",
  "settings.simulation.reset.desc":
    "Restore AC, transient and temperature fields to factory values.",
  "settings.simulation.reset.button": "Reset",
  // Sprint 24 A4 — appearance toggle option labels.
  "settings.appearance.canvas.background.option.night": "Night",
  "settings.appearance.canvas.background.option.technical": "Technical",
  "settings.appearance.canvas.background.option.paper": "Paper",
  "settings.appearance.canvas.background.option.blank": "Blank",
  "settings.appearance.canvas.wire.option.catenary": "Catenary",
  "settings.appearance.canvas.wire.option.manhattan": "Manhattan",
  "settings.appearance.canvas.wire.option.linear": "Linear",
  "settings.appearance.canvas.symbol.option.iec": "IEC",
  "settings.appearance.canvas.symbol.option.ansi": "ANSI",
  "settings.shortcuts.title": "Keyboard Shortcuts",
  "settings.shortcuts.sub": "Cmd/Ctrl ⌘ — same shortcuts on Mac and PC.",
  "settings.shortcuts.list.label": "Shortcuts",
  "settings.shortcuts.row.palette": "Toggle command palette",
  "settings.shortcuts.row.help": "Shortcut overlay",
  "settings.shortcuts.row.runDc": "Run DC",
  "settings.shortcuts.row.runTr": "Run transient (one-shot)",
  "settings.shortcuts.row.toggleLive": "Live mode toggle",
  "settings.shortcuts.row.waveform": "Toggle waveform panel",
  "settings.shortcuts.row.import": "Import dialog",
  "settings.shortcuts.row.export": "Export dialog",
  "settings.shortcuts.row.preset": "Open presets",
  "settings.shortcuts.row.pane": "Pane navigation",
  "settings.a11y.title": "Accessibility",
  "settings.a11y.sub": "WCAG AA compliant, motion reduction, contrast modes.",
  "settings.a11y.system.label": "System settings",
  "settings.a11y.reduceMotion.title": "Reduce motion",
  "settings.a11y.reduceMotion.desc":
    "Honors prefers-reduced-motion — restrains animations (first boot follows system pref)",
  // Sprint 80.Z L-AYARLAR-2 — Reduce-motion toggle option labels.
  "settings.a11y.reduceMotion.option.off": "Off",
  "settings.a11y.reduceMotion.option.on": "On",
  "settings.a11y.contrast.title": "Contrast mode",
  "settings.a11y.contrast.desc": "High contrast — for low-vision users",
  // Sprint 80.Z B1 — A11y contrast toggle option labels.
  "settings.a11y.contrast.option.normal": "Normal",
  "settings.a11y.contrast.option.high": "High",
  "settings.a11y.fontSize.title": "Font size",
  "settings.a11y.fontSize.desc": "Small → Large (S/M/L/XL)",
  "settings.about.title": "About",
  "settings.about.sub": "Version, license, studio info.",
  "settings.about.version.label": "Version",
  "settings.about.codename": "Reference Parity",
  "settings.about.version.body":
    "Volt × Ampere v3 — TypeScript monorepo, Lit web component, ngspice parity engine. Opens in your browser, no signup.",
  "settings.about.license.label": "License",
  "settings.about.license.body":
    "MIT — open source, commercial use allowed. Engine + UI packages MIT; app shell is proprietary.",
  "settings.about.studio.label": "Studio",
  "settings.about.studio.body":
    "360 Studio · Göksen Olgun · MMXXVI. An instrument of observation — circuits and measurement, nothing else.",
  "settings.about.cta.title": "Back to simulator",
  "settings.about.cta.desc": "Done reading — back to the circuit.",
  "settings.about.cta.action": "Open →",

  // Sprint 18 Paket B B6 — INSPECTOR detail page
  "inspector.page.caption": "Selected Component · Detail View",
  "inspector.page.empty.title": "No component selected",
  "inspector.page.empty.hint":
    "Return to the simulator and click a component. This detail view magnifies the selected element.",
  "inspector.page.empty.cta": "Back to simulator →",
  "inspector.page.section.value": "Value",
  "inspector.page.section.params": "Parameters",
  "inspector.page.section.actions": "Actions",
  "inspector.page.action.editInSim": "Edit in simulator →",
  "inspector.page.params.empty": "No numerical parameters.",
  "inspector.page.live.bindHint": "Bind a probe to a channel — values stream here.",
  "inspector.page.live.voltage": "Voltage (RMS)",
  "inspector.page.live.voltage.caption": "Effective value — equivalent steady heating",
  "inspector.page.live.peak": "Peak Voltage",
  "inspector.page.live.peak.caption": "Largest absolute sample",
  "inspector.page.live.peakToPeak": "Peak-to-Peak",
  "inspector.page.live.peakToPeak.caption": "Maximum swing amplitude",
  "inspector.page.live.average": "Average Voltage",
  "inspector.page.live.average.caption": "DC component — period mean",
  "inspector.page.subline.generic":
    "A single trace in the circuit; the path of current is read here.",

  // Sprint 18 Paket B B3 — Landing page
  "landing.caption.observatorium": "Observatorium Electricum",
  "landing.hero.subtitle": "an instrument of observation.",
  "landing.hero.lede":
    "Opens in your browser, <em>LTspice</em> accurate, <em>Falstad</em> fluid. No accounts, no fees. Only circuits, only measurement, only the <em>trace of current</em>.",
  // Sprint 23 E1 — D-23-1 hibrit closure.
  "landing.cta.openSimulator": "Go to simulator →",
  "landing.cta.presets": "Demo circuits",
  "landing.feature.openSource.title": "Open source",
  "landing.feature.openSource.desc":
    "Engine open under MIT. Every sentinel-tested formula belongs to the public — use it, fork it, improve it.",
  "landing.sep.what": "What it does",
  "landing.feature.accuracy.title": "It does not err.",
  "landing.feature.accuracy.desc":
    "Modified Nodal Analysis, Newton-Raphson convergence, Cuthill-McKee reorder. Within ±0.15V parity of <em>ngspice</em>.",
  "landing.feature.live.title": "It feels alive.",
  "landing.feature.live.desc":
    "Current flows along wires like meteors. Voltage reads as color. Every measurement — <em>before the whisper</em>.",
  "landing.feature.localized.title": "Turkish first.",
  "landing.feature.localized.desc":
    "Resistor, capacitor, diode. Not in the language of science, but <em>your own language</em>. The English stands behind the terms.",
  "landing.stat.components": "components",
  "landing.stat.tests": "unit tests",
  "landing.stat.dependencies": "dependencies",
  "landing.stat.parity": "ngspice parity",
  "landing.manifesto.label": "Manifesto",
  "landing.manifesto.text":
    '"A circuit is not <em>viewed</em>, it is <em>felt</em>. Every electron is a <em>traveler</em>; to <em>read</em> its course you need an instrument of observation."',
  "landing.manifesto.sign": "— Volt × Ampere · Observatorium · MMXXVI",

  // Sidebar
  "sidebar.search.placeholder": "Search symbols…",
  "sidebar.section.sources": "Sources",
  "sidebar.section.passive": "Passive",
  "sidebar.section.active": "Active",
  "sidebar.section.connectors": "Connectors",
  "sidebar.empty": "No matches",
  "sidebar.templates.heading": "Templates",
  "sidebar.templates.placeholder": "Templates land in Sprint 18.",

  // Canvas / Inspector placeholders
  "canvas.placeholder.title": "Canvas — Sprint 12",
  "canvas.placeholder.subtitle": "Schematic editing arrives next sprint.",
  "inspector.placeholder.title": "Inspector — Sprint 14",
  "inspector.placeholder.subtitle": "Component properties will surface here.",

  // Inspector — Sprint 14
  "inspector.empty.title": "No component selected",
  "inspector.empty.subtitle": "Select a component or press Cmd+A for all.",
  "inspector.mixed.title": "Mixed selection",
  "inspector.mixed.subtitle": "Different component types selected — no shared parameters.",
  "inspector.section.params": "Parameters",
  "inspector.section.formula": "Formula",
  "inspector.section.net": "Net",
  "inspector.section.measurements": "Live measurements",
  // Sprint 18 Paket C C3 — action row
  "inspector.section.actions": "Actions",
  "inspector.action.rotate": "Rotate",
  "inspector.action.copy": "Copy",
  "inspector.action.delete": "Delete",

  // Sprint 18 D6 — breadcrumb
  "inspector.breadcrumb.component": "Component",
  "inspector.breadcrumb.properties": "Properties",
  "inspector.breadcrumb.net": "Net",
  "inspector.breadcrumb.selection": "Selection",

  // Sprint 18 D4 — live measurement table labels
  "inspector.measurements.empty": "No live data — bind a probe channel to this net",
  "inspector.measurements.vrms": "RMS voltage",
  "inspector.measurements.vpeak": "Peak voltage",
  "inspector.measurements.vpp": "Peak-to-peak",
  "inspector.measurements.vavg": "Average voltage",
  "inspector.measurements.vmax": "Max voltage",
  "inspector.measurements.vmin": "Min voltage",
  "inspector.measurements.frequency": "Frequency",
  "inspector.measurements.period": "Period",
  // Sprint 34-4 — thermal inspector section.
  "inspector.thermal.title": "Temperature",
  "inspector.thermal.noData": "No thermal data — runs when the circuit is live",
  "inspector.thermal.temperature": "Temperature",
  "inspector.thermal.tMax": "Max rated",
  "inspector.thermal.power": "Power dissipation",
  "inspector.thermal.severity": "Load",
  "inspector.button.runDc": "Run DC (F5)",
  "inspector.button.delete.annotation": "Delete annotation",
  "inspector.label.netName": "Net name",
  "inspector.hint.gnd": "The ground net's name cannot be changed.",
  "inspector.dc.idle": "No DC result",
  "inspector.dc.running": "Running DC…",
  "inspector.dc.error": "DC error: {message}",
  "inspector.formula.missing": "No formula registered for this component.",
  "inspector.multi.heading": "{count} components — common parameters",
  // Param labels
  "inspector.param.resistance": "Resistance",
  "inspector.param.capacitance": "Capacitance",
  "inspector.param.inductance": "Inductance",
  "inspector.param.voltage": "Voltage",
  "inspector.param.frequency": "Frequency",
  "inspector.param.phase": "Phase",
  "inspector.param.amplitude": "Amplitude",
  "inspector.param.tolerance": "Tolerance",
  "inspector.param.powerRating": "Power rating",
  "inspector.param.saturationCurrent": "Saturation current",
  "inspector.param.emissionCoefficient": "Emission coefficient",
  "inspector.param.forwardVoltage": "Forward voltage",
  "inspector.param.zenerVoltage": "Zener voltage",
  "inspector.param.beta": "β (gain)",
  "inspector.param.kp": "Kp (transconductance)",
  "inspector.param.thresholdVoltage": "Threshold voltage",
  "inspector.param.channelWidth": "Channel width",
  "inspector.param.channelLength": "Channel length",
  "inspector.param.idss": "IDSS",
  "inspector.param.pinchOff": "Pinch-off voltage",
  "inspector.param.openLoopGain": "Open-loop gain",
  "inspector.param.saturationVoltage": "Saturation voltage",
  "inspector.param.closed": "Closed",
  "inspector.param.wiperRatio": "Wiper ratio",
  "inspector.param.position": "Position",
  "inspector.param.channelMod": "Channel modulation",
  "inspector.param.sourceKind": "Source kind",

  // Sprint 23 C2 — onboarding tour
  "tour.welcome.title": "Welcome to VoltXAmpere",
  "tour.welcome.body":
    "A browser-based electronics lab with LTspice-grade accuracy. Let me show you around in a few steps.",
  "tour.sidebar.title": "Pick a component from the sidebar",
  "tour.sidebar.body":
    "The symbol library on the left lists every component — resistors, capacitors, transistors. Drag one onto the canvas.",
  "tour.canvas.title": "Place it on the canvas",
  "tour.canvas.body":
    "Click and drag to position components. Press W to start a wire and connect pins.",
  "tour.simulate.title": "Press F5 to simulate",
  "tour.simulate.body":
    "Once your circuit is wired up, hit F5 to run DC analysis. Inspector tiles show live voltages and currents.",
  "tour.ac.title": "Press F6 for AC analysis",
  "tour.ac.body":
    "Hit F6 to see frequency response — the Bode plot draws gain and phase across your sweep range.",
  "tour.palette.title": "Cmd+K for everything",
  "tour.palette.body":
    "Cmd+K (or Ctrl+K) opens the command palette. Placement, simulation, settings — fuzzy-search to anything.",
  "tour.skip": "Skip",
  "tour.next": "Next",
  "tour.prev": "Previous",
  "tour.done": "Finish",
  "tour.reset": "Show tour again",

  // Sprint 23 D1+D2 — custom shortcut UI
  "settings.shortcuts.edit": "Edit",
  "settings.shortcuts.reset": "Reset",
  "settings.shortcuts.resetAll": "Reset all changes",
  "settings.shortcuts.resetConfirm": "Reset every customised shortcut back to its default?",
  "settings.shortcuts.editPrompt":
    "New shortcut combo (e.g. Mod+s, Shift+P; leave blank to disable):",
  "settings.shortcuts.conflict": "Will overwrite {{command}} — confirm?",
  "settings.shortcuts.overridden": "Customised",
  "settings.shortcuts.disabled": "disabled",

  // Sprint 23 D3 — OpAmp model presets
  "inspector.opamp.preset.label": "Model",
  "inspector.opamp.preset.custom": "Custom",
  "inspector.opamp.preset.ua741": "µA741",
  "inspector.opamp.preset.lm358": "LM358",
  "inspector.opamp.preset.tl072": "TL072",

  // Sprint 23 I1 — topbar save + auto-save indicator
  "topbar.save": "Save",
  "topbar.autoSave.pending": "Pending…",
  "topbar.autoSave.saving": "Saving…",
  "topbar.autoSave.saved": "Saved",
  "topbar.autoSave.savedAt": "Saved at {{time}}",
  "topbar.autoSave.error": "Save error",
  "shortcut.file.save": "Save circuit",

  // Sprint 24 A2 — Bode UX overlay
  "bode.subplot.mag": "|H(jω)| — gain",
  "bode.subplot.phase": "∠H(jω) — phase",
  "bode.axis.frequency": "Frequency (Hz)",
  "bode.axis.magnitude": "Gain",
  "bode.axis.phase": "Phase",

  // Statusbar
  "statusbar.ready": "Ready",
  "statusbar.symbols": "{count} symbols",
  "statusbar.selection": "{count} selected",
  "statusbar.locale": "EN",

  // Command palette
  "palette.title": "Command Palette",
  "palette.search": "Search commands…",
  "palette.placeholder": "Commands arrive in Sprint 13.",
  "palette.hint": "Press Esc to close",

  // Shortcut overlay
  "shortcuts.title": "Keyboard Shortcuts",
  "shortcuts.section.global": "Global",
  "shortcuts.section.symbols": "Symbols",
  "shortcuts.empty": "No shortcuts registered.",
  "shortcuts.hint": "Press Esc to close",

  // Modal generic
  "modal.close": "Close",

  // Shortcut descriptions
  "shortcut.palette.toggle": "Toggle command palette",
  "shortcut.shortcuts.toggle": "Shortcut overlay",
  // Sprint 80.Y L-A-2 — oscilloscope reversibility (Şefim #2 closure).
  "shortcut.view.toggleWaveformPane": "Toggle oscilloscope pane",
  "shortcut.modal.close": "Close modal",
  "shortcut.symbol.arm": "Add {name}",
  // Sprint 27 Lane 2 — typecheck cleanup; see TR bundle for context.
  "shortcut.delete.describe": "Delete selected",
  "canvas.aria.label": "Circuit design canvas",

  // Import / Export — Sprint 16
  "topbar.import": "Import",
  "topbar.export": "Export",
  // Sprint 80.Y L-A-2 — oscilloscope topbar toggle chip.
  "topbar.waveform.toggle.label": "Scope",
  "topbar.waveform.toggle.title": "Toggle oscilloscope pane (⌘⇧O)",
  "topbar.waveform.toggle.hide": "Hide oscilloscope pane",
  "topbar.waveform.toggle.show": "Show oscilloscope pane",
  "import.title": "Import SPICE Circuit",
  "import.samples.label": "Sample circuits",
  "import.paste.label": "Paste your SPICE netlist",
  "import.paste.placeholder": "* Voltage divider\nV1 vcc 0 5\nR1 vcc out 1k\nR2 out 0 1k\n.end",
  "import.button.cancel": "Cancel",
  "import.button.preview": "Preview",
  "import.button.apply": "Apply",
  "import.preview.summary": "{components} components · {nets} nets · {warnings} warnings",
  "import.error.line": "Line {line}:{col} — {message}",
  "export.title": "Export as SPICE",
  "export.preview.label": "SPICE output (canonical Berkeley SPICE3)",
  "export.button.close": "Close",
  "export.button.copy": "Copy to Clipboard",
  "export.button.copied": "Copied!",
  "export.button.download": "Download .cir",
  "export.empty": "No active circuit",
  "shortcut.import.open": "Import dialog",
  "shortcut.export.open": "Export dialog",
  "shortcut.export.clipboard": "Quick export to clipboard",

  // Presets — Sprint 17
  "topbar.presets": "Presets",
  "presets.title": "Demo Circuits",
  "presets.category.all": "All",
  // Sprint 18 Paket B B4 — Circuits page
  "presets.page.caption": "Catalogus Circuitorum",
  "presets.page.title": "Demo <em>circuits</em>",
  "presets.page.sub":
    "Common reference circuits — from voltage dividers to the 555 oscillator. Open each one, read it, play with it.",
  // Sprint 80.Y L-D-1 — total preset/category count badge (Şefim
  // #5/#6 discoverability closure).
  "presets.page.totalBadge": "{count} presets · {categories} categories",
  "presets.empty": "This category is empty for now — soon.",
  "presets.button.cancel": "Cancel",
  "presets.button.load": "Load",
  "presets.detail.empty": "Select a circuit on the left to preview.",
  "presets.expectedValues.label": "Expected Values",
  "presets.expectedValues.empty": "No numerical verification yet for this preset.",
  "presets.learningGoals.label": "Learning Goals",
  "presets.references.label": "References",
  "shortcut.presets.open": "Open demo circuits",

  // Sprint 18 Paket C C1 — Δt cursor pair
  "waveform.cursor.t1": "T1",
  "waveform.cursor.t2": "T2",
  "waveform.cursor.dt": "Δt",
  "waveform.cursor.invdt": "1/Δt",
  "waveform.cursor.toggle.title": "Δt cursors",
  "waveform.cursor.toggle.aria": "Toggle Δt cursors",
  "shortcut.waveform.cursor.t1": "Move T1 cursor",
  "shortcut.waveform.cursor.t2": "Move T2 cursor (Shift)",

  // Sprint 18 Paket C C2 — 8/8 measurement tiles
  "waveform.measure.vrms": "V_rms",
  "waveform.measure.vpeak": "V_peak",
  "waveform.measure.vpp": "V_pp",
  "waveform.measure.frequency": "Frequency",
  "waveform.measure.period": "Period",
  "waveform.measure.duty": "Duty",
  "waveform.measure.deltaT": "Δt",
  "waveform.measure.invDeltaT": "1/Δt",
  "waveform.empty.title": "No probe attached",
  "waveform.empty.hint": "Right-click a wire on the canvas to bind a channel.",
  // Sprint 19 F4 — probe context menu localization
  "probeMenu.netHeader": "Net: {netId}…",
  "probeMenu.bind": "Probe to {channel}",
  "probeMenu.boundHere": "✓ {channel} (this net)",
  "probeMenu.boundElsewhere": "{channel} — bound elsewhere",
  // Sprint 25 R3 lane 4 — auto-bind to next free channel
  "probeMenu.autoBind": "★ Auto-bind → {channel}",
  // Sprint 20 — AC analysis tokens
  "ac.run.label": "Run AC analysis (F6)",
  "ac.config.fStart": "Start frequency (Hz)",
  "ac.config.fStop": "Stop frequency (Hz)",
  "ac.config.points": "Number of points",
  "ac.config.sweep.dec": "Logarithmic (dec)",
  "ac.config.sweep.oct": "Logarithmic (oct)",
  "ac.config.sweep.lin": "Linear",
  "ac.mode.magnitude": "Magnitude",
  "ac.mode.phase": "Phase",
  "ac.units.linear": "Linear",
  "ac.units.dB": "dB",
  "ac.units.deg": "°",
  "ac.units.rad": "rad",
  "ac.empty.title": "No AC result",
  "ac.empty.hint": "Press F6 to start an AC sweep.",
  "ac.errors.fStartInvalid": "Start frequency must be positive.",
  "ac.errors.fStartGreaterEqStop": "Start must be less than stop.",
  "ac.errors.pointsInvalid": "At least 2 points are required.",
  "ac.status.running": "AC running… {points} points",
  "ac.status.done": "AC: {points} points · {ms} ms",
  // Sprint 21 C4 — URL hash share tokens
  "share.url.button": "Share link",
  "share.url.copy.success": "Link copied to clipboard",
  "share.url.copy.fail": "Could not copy link: {reason}",
  "share.url.tooLarge.title": "Circuit too large to share via URL",
  "share.url.tooLarge.hint": "Download as .vxa instead (no size limit).",
  "share.url.import.success": "Shared circuit loaded",
  "share.url.import.fail": "Failed to decode shared link: {reason}",
  "share.url.toast.shared": "Link copied · {bytes} bytes",
  // Sprint 21 C5 — AYARLAR Data tab
  "settings.tab.data": "Data",
  "settings.data.title": "Data management",
  "settings.data.storage.label": "Storage",
  "settings.data.storage.circuits": "Circuits",
  "settings.data.storage.bytes": "Size",
  "settings.data.recents.label": "Recent circuits",
  "settings.data.recents.empty": "No saved circuits yet.",
  "settings.data.recents.open": "Open",
  "settings.data.recents.delete": "Delete",
  "settings.data.actions.label": "Actions",
  "settings.data.import.button": "Import .vxa",
  "settings.data.import.success": "Circuit loaded.",
  "settings.data.import.fail": "Import failed: {reason}",
  "settings.data.clear.button": "Clear all",
  "settings.data.clear.confirm": "All circuit data will be erased. Cannot be undone.",
  "settings.data.clear.confirm.yes": "Yes, delete",
  "settings.data.clear.confirm.no": "Cancel",
  "settings.data.clear.success": "All data cleared.",
  // Sprint 21 C7 — AC config dialog (Sprint 20 C6 deferred)
  "ac.config.title": "AC sweep settings",
  "ac.config.sweepLabel": "Sweep distribution",
  "ac.config.run": "Run AC",
  "ac.config.cancel": "Cancel",

  // Sprint 22 E1 — Command palette (BLUEPRINT §14) helper strings
  "palette.recent.label": "Recent",
  "palette.suggested.label": "Suggested",
  "palette.empty.title": "No matching command",
  "palette.empty.hint": "Try another keyword or press Esc to close.",
  "palette.executing": "Running…",
  "palette.error.notFound": "Command not found: {id}",
  "palette.error.gated": "This command is unavailable in the current context.",
  "palette.shortcut.label": "Shortcut",

  // Sprint 22 E1 — file.* (10 commands after S80.Z Phase 4 D1
  // removed file.saveAs; file.saveCopy retained but gated when:false)
  "command.file.save": "Save circuit",
  "command.file.save.desc": "Persist the active circuit to browser storage",
  "command.file.saveCopy": "Save a copy",
  "command.file.exportVxa": "Export as .vxa",
  "command.file.exportVxa.desc": "Download the circuit as a portable .vxa file",
  "command.file.importVxa": "Import .vxa",
  "command.file.importVxa.desc": "Open a previously saved .vxa file",
  "command.file.shareUrl": "Share link",
  "command.file.shareUrl.desc": "Pack the circuit into a URL hash and copy to clipboard",
  "command.file.newEmpty": "New empty circuit",
  "command.file.newEmpty.desc": "Clear the current circuit and start over",
  "command.file.newFromTemplate": "New from template",
  "command.file.newFromTemplate.desc": "Pick a starting point from the demo circuit catalog",
  "command.file.recent.slot1": "Recent · 1",
  "command.file.recent.slot2": "Recent · 2",
  "command.file.recent.slot3": "Recent · 3",

  // Sprint 22 E1 — edit.* (8 commands after S80.Z Phase 4 D1
  // removed copy/cut/paste/duplicate/flipHorizontal/flipVertical)
  "command.edit.undo": "Undo",
  "command.edit.undo.desc": "Reverse the last change",
  "command.edit.redo": "Redo",
  "command.edit.redo.desc": "Reapply the last undone change",
  "command.edit.delete": "Delete",
  "command.edit.selectAll": "Select all",
  "command.edit.deselect": "Deselect",
  "command.edit.rotate": "Rotate",
  "command.edit.rotate.desc": "Rotate the selected component 90° clockwise",
  "command.edit.rotateCw": "Rotate clockwise",
  "command.edit.rotateCcw": "Rotate counter-clockwise",

  // Sprint 22 E1 — view.* (12 commands)
  "command.view.zoomIn": "Zoom in",
  "command.view.zoomOut": "Zoom out",
  "command.view.zoomFit": "Fit to screen",
  "command.view.zoomReset": "Reset zoom",
  "command.view.toggleGrid": "Toggle grid",
  "command.view.toggleSnap": "Toggle snap",
  "command.view.toggleSnap.desc": "Whether components snap to the grid",
  "command.view.toggleDarkMode": "Toggle dark mode",
  "command.view.toggleDarkMode.desc": "Switch to Zenit night mode (light is default)",
  "command.view.toggleFullscreen": "Toggle fullscreen",
  "command.view.toggleInspector": "Toggle inspector panel",
  "command.view.toggleBode": "Toggle Bode plot",
  "command.view.toggleBode.desc": "Show or hide the AC analysis Bode plot panel",
  "command.view.toggleCursors": "Toggle Δt cursors",
  "command.view.toggleCursors.desc": "Toggle T1/T2 measurement cursors on the waveform",
  "command.view.toggleLabels": "Toggle net labels",
  // Sprint 80.Y L-A-2 — palette command (Şefim #2 closure).
  "command.view.toggleWaveformPane": "Toggle oscilloscope pane",
  "command.view.toggleWaveformPane.desc":
    "Fully hide or show the bottom oscilloscope pane — same as Cmd+Shift+O",

  // Sprint 22 E1 — tool.* (6 commands)
  "command.tool.select": "Select tool",
  "command.tool.select.desc": "Return to the arrow tool for clicking and dragging",
  "command.tool.wire": "Wire tool",
  "command.tool.wire.desc": "Draw a wire between two nets",
  "command.tool.probe": "Probe tool",
  "command.tool.probe.desc": "Drop a probe on a net and bind it to a channel",
  "command.tool.stamp": "Stamp tool",
  "command.tool.stamp.desc": "Pick a symbol from the library and place it on the canvas",
  "command.tool.text": "Text tool",
  "command.tool.pan": "Pan tool",

  // Sprint 22 E1 — place.* (20 commands, ALL_SYMBOLS order)
  "command.place.dc-source": "Place DC source",
  "command.place.dc-source.desc": "Constant voltage source (e.g. V₁)",
  "command.place.ac-source": "Place AC source",
  "command.place.ac-source.desc": "Sine voltage source — small-signal in AC analysis",
  "command.place.pulse-source": "Place pulse source",
  "command.place.pulse-source.desc": "Square/digital pulse — V₁→V₂ pulse trains",
  // Sprint 25 R2 lane 4b — current-source UI symbol
  "command.place.current-source": "Place current source",
  "command.place.current-source.desc": "Constant DC current source (e.g. I₁)",
  // Sprint 25 R2 lane 4c — pwl-source UI symbol
  "command.place.pwl-source": "Place PWL source",
  "command.place.pwl-source.desc": "Piecewise-linear waveform — (time, V) breakpoint list",
  "command.place.resistor": "Place resistor",
  "command.place.resistor.desc": "Two-terminal resistor (Ω)",
  "command.place.capacitor": "Place capacitor",
  "command.place.capacitor.desc": "Two-terminal capacitor (F)",
  "command.place.inductor": "Place inductor",
  "command.place.inductor.desc": "Two-terminal inductor (H)",
  "command.place.potentiometer": "Place potentiometer",
  "command.place.potentiometer.desc": "Three-terminal adjustable resistor (wiper ratio)",
  "command.place.switch": "Place switch",
  "command.place.switch.desc": "Manually controlled open/closed switch",
  "command.place.ground": "Place ground",
  "command.place.ground.desc": "0 V reference point — required in every circuit",
  "command.place.wire": "Place wire",
  "command.place.wire.desc": "Wire segment connecting two endpoints",
  "command.place.node": "Place junction",
  "command.place.node.desc": "Junction where three or more wires meet",
  "command.place.diode": "Place diode",
  "command.place.diode.desc": "One-way conduction — anode → cathode",
  "command.place.led": "Place LED",
  "command.place.led.desc": "Light-emitting diode — V_F ≈ 1.8…3.4 V",
  "command.place.zener": "Place zener diode",
  "command.place.zener.desc": "Conducts in reverse at fixed V_Z",
  "command.place.npn": "Place NPN transistor",
  "command.place.npn.desc": "Bipolar NPN — Ebers-Moll, the canonical amplifier",
  "command.place.pnp": "Place PNP transistor",
  "command.place.pnp.desc": "Bipolar PNP — opposite-polarity sibling of NPN",
  "command.place.nmos": "Place NMOS transistor",
  "command.place.nmos.desc": "Shichman-Hodges N-channel MOSFET",
  "command.place.pmos": "Place PMOS transistor",
  "command.place.pmos.desc": "Shichman-Hodges P-channel MOSFET",
  "command.place.jfet": "Place JFET",
  "command.place.jfet.desc": "Junction FET — high input impedance",
  "command.place.op-amp": "Place op-amp",
  "command.place.op-amp.desc": "Single-pole ideal/macro operational amplifier",

  // Sprint 80.X Sandık Kapağı (F-IA-101, ADR-0118) — i18n backfill for the 184 Sprint 38+ datasheet variant place commands. script-generated via scripts/i18n/generate-variant-tokens.mjs.
  "command.place.headphone-amp": "Headphone Amp (CMoy generic, placeholder)",
  "command.place.headphone-amp.desc": "Place Headphone Amp (CMoy generic, placeholder)",
  "command.place.lm386": "LM386 (Audio Amp 0.5W, DIP-8, placeholder)",
  "command.place.lm386.desc": "Place LM386 (Audio Amp 0.5W, DIP-8, placeholder)",
  "command.place.pam8403": "PAM8403 (Class-D Stereo 2×3W, SOP-16, placeholder)",
  "command.place.pam8403.desc": "Place PAM8403 (Class-D Stereo 2×3W, SOP-16, placeholder)",
  "command.place.tda2030": "TDA2030A (Audio Amp 18W, TO-220-5, placeholder)",
  "command.place.tda2030.desc": "Place TDA2030A (Audio Amp 18W, TO-220-5, placeholder)",
  "command.place.tda7297": "TDA7297 (Stereo Audio Amp 2×15W, MW-15, placeholder)",
  "command.place.tda7297.desc": "Place TDA7297 (Stereo Audio Amp 2×15W, MW-15, placeholder)",
  "command.place.battery-9v-alkaline": "9V Alkaline Battery (NEDA 1604)",
  "command.place.battery-9v-alkaline.desc": "Place 9V Alkaline Battery (NEDA 1604)",
  "command.place.2n2222": "2N2222 (NPN, TO92)",
  "command.place.2n2222.desc": "Place 2N2222 (NPN, TO92)",
  "command.place.2n3904": "2N3904 (NPN, TO92)",
  "command.place.2n3904.desc": "Place 2N3904 (NPN, TO92)",
  "command.place.bc547": "BC547 (NPN, TO92)",
  "command.place.bc547.desc": "Place BC547 (NPN, TO92)",
  "command.place.2n3906": "2N3906 (PNP, TO92)",
  "command.place.2n3906.desc": "Place 2N3906 (PNP, TO92)",
  "command.place.bc557": "BC557 (PNP, TO92)",
  "command.place.bc557.desc": "Place BC557 (PNP, TO92)",
  "command.place.buzzer-active-5v": "Active Piezo Buzzer 5V (built-in oscillator)",
  "command.place.buzzer-active-5v.desc": "Place Active Piezo Buzzer 5V (built-in oscillator)",
  "command.place.buzzer-passive-5v": "Passive Piezo Buzzer 5V (PWM controlled)",
  "command.place.buzzer-passive-5v.desc": "Place Passive Piezo Buzzer 5V (PWM controlled)",
  "command.place.dtr-rts-reset": "DTR/RTS Auto-Reset Module (Arduino)",
  "command.place.dtr-rts-reset.desc": "Place DTR/RTS Auto-Reset Module (Arduino)",
  "command.place.battery-9v-clip": "9V Battery Clip (PP3 Snap)",
  "command.place.battery-9v-clip.desc": "Place 9V Battery Clip (PP3 Snap)",
  "command.place.alligator-clips": "Alligator Clip Pair (red + black)",
  "command.place.alligator-clips.desc": "Place Alligator Clip Pair (red + black)",
  "command.place.db9-male": "DB9 Male (RS-232 Connector)",
  "command.place.db9-male.desc": "Place DB9 Male (RS-232 Connector)",
  "command.place.din-5pin": "5-Pin DIN Connector (audio/MIDI)",
  "command.place.din-5pin.desc": "Place 5-Pin DIN Connector (audio/MIDI)",
  "command.place.icsp-header": "ICSP Header (6-pin AVR Programming)",
  "command.place.icsp-header.desc": "Place ICSP Header (6-pin AVR Programming)",
  "command.place.jst-ph": "JST PH 2.0mm (2-pin power, schema-only)",
  "command.place.jst-ph.desc": "Place JST PH 2.0mm (2-pin power, schema-only)",
  "command.place.jtag-header": "JTAG Header (10-pin Atmel ICE)",
  "command.place.jtag-header.desc": "Place JTAG Header (10-pin Atmel ICE)",
  "command.place.pin-header-2.54": "Pin Header 2.54mm (single row, schema-only)",
  "command.place.pin-header-2.54.desc": "Place Pin Header 2.54mm (single row, schema-only)",
  "command.place.rs232-cable": "RS-232 Null-Modem Cable",
  "command.place.rs232-cable.desc": "Place RS-232 Null-Modem Cable",
  "command.place.screw-terminal": "Screw Terminal Block (PCB-mount, schema-only)",
  "command.place.screw-terminal.desc": "Place Screw Terminal Block (PCB-mount, schema-only)",
  "command.place.usb-a": "USB-A (Type-A 4-pin, schema-only)",
  "command.place.usb-a.desc": "Place USB-A (Type-A 4-pin, schema-only)",
  "command.place.usb-c": "USB-C (Type-C 24-pin, schema-only)",
  "command.place.usb-c.desc": "Place USB-C (Type-C 24-pin, schema-only)",
  "command.place.crystal-16mhz": "Crystal 16 MHz (HC-49S, placeholder)",
  "command.place.crystal-16mhz.desc": "Place Crystal 16 MHz (HC-49S, placeholder)",
  "command.place.crystal-32k768": "Crystal 32.768 kHz (Watch, RTC, placeholder)",
  "command.place.crystal-32k768.desc": "Place Crystal 32.768 kHz (Watch, RTC, placeholder)",
  "command.place.ceramic-resonator": "Ceramic Resonator 8 MHz (Murata CSTLS, placeholder)",
  "command.place.ceramic-resonator.desc":
    "Place Ceramic Resonator 8 MHz (Murata CSTLS, placeholder)",
  "command.place.avalanche-diode": "Avalanche Diode 100V (APD, placeholder)",
  "command.place.avalanche-diode.desc": "Place Avalanche Diode 100V (APD, placeholder)",
  "command.place.photodiode-array": "Photodiode Array (Hamamatsu-class, placeholder)",
  "command.place.photodiode-array.desc": "Place Photodiode Array (Hamamatsu-class, placeholder)",
  "command.place.tunnel-diode": "Tunnel Diode (Esaki, NDR, placeholder)",
  "command.place.tunnel-diode.desc": "Place Tunnel Diode (Esaki, NDR, placeholder)",
  "command.place.p6ke6v8": "P6KE6.8A (TVS 6.8V, DO-15, placeholder)",
  "command.place.p6ke6v8.desc": "Place P6KE6.8A (TVS 6.8V, DO-15, placeholder)",
  "command.place.varactor": "Varactor 1SV101 (variable cap, placeholder)",
  "command.place.varactor.desc": "Place Varactor 1SV101 (variable cap, placeholder)",
  "command.place.7seg-ca": "7-Segment Display (Common Anode, 5161AH, placeholder)",
  "command.place.7seg-ca.desc": "Place 7-Segment Display (Common Anode, 5161AH, placeholder)",
  "command.place.7seg-cc": "7-Segment Display (Common Cathode, 5161AS, placeholder)",
  "command.place.7seg-cc.desc": "Place 7-Segment Display (Common Cathode, 5161AS, placeholder)",
  "command.place.lcd-16x2": "16x2 Character LCD (HD44780, placeholder)",
  "command.place.lcd-16x2.desc": "Place 16x2 Character LCD (HD44780, placeholder)",
  "command.place.lcd-20x4": "20x4 Character LCD (HD44780, placeholder)",
  "command.place.lcd-20x4.desc": "Place 20x4 Character LCD (HD44780, placeholder)",
  "command.place.led-matrix-8x8": "8x8 LED Matrix (1088AS, placeholder)",
  "command.place.led-matrix-8x8.desc": "Place 8x8 LED Matrix (1088AS, placeholder)",
  "command.place.ch340g": "CH340G (USB-UART Bridge, low-cost)",
  "command.place.ch340g.desc": "Place CH340G (USB-UART Bridge, low-cost)",
  "command.place.cp2102": "CP2102 (USB-UART Bridge)",
  "command.place.cp2102.desc": "Place CP2102 (USB-UART Bridge)",
  "command.place.ft232rl": "FT232RL (USB-UART Bridge)",
  "command.place.ft232rl.desc": "Place FT232RL (USB-UART Bridge)",
  "command.place.max232": "MAX232 (TTL ↔ RS-232 Level Shifter)",
  "command.place.max232.desc": "Place MAX232 (TTL ↔ RS-232 Level Shifter)",
  "command.place.pc817-uart": "PC817 Opto-Isolated UART",
  "command.place.pc817-uart.desc": "Place PC817 Opto-Isolated UART",
  "command.place.2n3819": "2N3819 (n-channel JFET, TO92)",
  "command.place.2n3819.desc": "Place 2N3819 (n-channel JFET, TO92)",
  "command.place.j201": "J201 (n-channel audio JFET, TO92)",
  "command.place.j201.desc": "Place J201 (n-channel audio JFET, TO92)",
  "command.place.led-blue-5mm": "LED Blue 5mm (generic)",
  "command.place.led-blue-5mm.desc": "Place LED Blue 5mm (generic)",
  "command.place.led-green-5mm": "LED Green 5mm (generic)",
  "command.place.led-green-5mm.desc": "Place LED Green 5mm (generic)",
  "command.place.led-red-5mm": "LED Red 5mm (generic)",
  "command.place.led-red-5mm.desc": "Place LED Red 5mm (generic)",
  "command.place.led-white-5mm": "LED White 5mm (generic)",
  "command.place.led-white-5mm.desc": "Place LED White 5mm (generic)",
  "command.place.led-yellow-5mm": "LED Yellow 5mm (generic)",
  "command.place.led-yellow-5mm.desc": "Place LED Yellow 5mm (generic)",
  "command.place.4001": "4001 (Quad NOR CMOS, DIP-14)",
  "command.place.4001.desc": "Place 4001 (Quad NOR CMOS, DIP-14)",
  "command.place.4011": "4011 (Quad NAND CMOS, DIP-14)",
  "command.place.4011.desc": "Place 4011 (Quad NAND CMOS, DIP-14)",
  "command.place.4017": "4017 (Decade Counter CMOS, DIP-16, placeholder)",
  "command.place.4017.desc": "Place 4017 (Decade Counter CMOS, DIP-16, placeholder)",
  "command.place.4051": "4051 (8-Channel Analog Mux CMOS, DIP-16)",
  "command.place.4051.desc": "Place 4051 (8-Channel Analog Mux CMOS, DIP-16)",
  "command.place.4052": "4052 (Dual 4-Channel Analog Mux CMOS, DIP-16)",
  "command.place.4052.desc": "Place 4052 (Dual 4-Channel Analog Mux CMOS, DIP-16)",
  "command.place.4067": "4067 (16-Channel Analog Mux CMOS, DIP-24)",
  "command.place.4067.desc": "Place 4067 (16-Channel Analog Mux CMOS, DIP-24)",
  "command.place.4070": "4070 (Quad XOR CMOS, DIP-14)",
  "command.place.4070.desc": "Place 4070 (Quad XOR CMOS, DIP-14)",
  "command.place.4584": "4584 (Hex Schmitt NOT CMOS, DIP-14)",
  "command.place.4584.desc": "Place 4584 (Hex Schmitt NOT CMOS, DIP-14)",
  "command.place.7400": "7400 (Quad NAND, DIP-14)",
  "command.place.7400.desc": "Place 7400 (Quad NAND, DIP-14)",
  "command.place.7402": "7402 (Quad NOR, DIP-14)",
  "command.place.7402.desc": "Place 7402 (Quad NOR, DIP-14)",
  "command.place.7404": "7404 (Hex Inverter, DIP-14)",
  "command.place.7404.desc": "Place 7404 (Hex Inverter, DIP-14)",
  "command.place.7408": "7408 (Quad AND, DIP-14)",
  "command.place.7408.desc": "Place 7408 (Quad AND, DIP-14)",
  "command.place.7432": "7432 (Quad OR, DIP-14)",
  "command.place.7432.desc": "Place 7432 (Quad OR, DIP-14)",
  "command.place.7474": "7474 (Dual D Flip-Flop, DIP-14, placeholder)",
  "command.place.7474.desc": "Place 7474 (Dual D Flip-Flop, DIP-14, placeholder)",
  "command.place.7486": "7486 (Quad XOR, DIP-14)",
  "command.place.7486.desc": "Place 7486 (Quad XOR, DIP-14)",
  "command.place.74hc00": "74HC00 (Quad NAND CMOS, DIP-14)",
  "command.place.74hc00.desc": "Place 74HC00 (Quad NAND CMOS, DIP-14)",
  "command.place.74hc04": "74HC04 (Hex Inverter CMOS, DIP-14)",
  "command.place.74hc04.desc": "Place 74HC04 (Hex Inverter CMOS, DIP-14)",
  "command.place.74hc08": "74HC08 (Quad AND CMOS, DIP-14)",
  "command.place.74hc08.desc": "Place 74HC08 (Quad AND CMOS, DIP-14)",
  "command.place.74hc132": "74HC132 (Quad Schmitt NAND, DIP-14)",
  "command.place.74hc132.desc": "Place 74HC132 (Quad Schmitt NAND, DIP-14)",
  "command.place.74hc14": "74HC14 (Hex Schmitt NOT, DIP-14)",
  "command.place.74hc14.desc": "Place 74HC14 (Hex Schmitt NOT, DIP-14)",
  "command.place.74hc4051": "74HC4051 (8-Channel High-Speed CMOS Mux, DIP-16)",
  "command.place.74hc4051.desc": "Place 74HC4051 (8-Channel High-Speed CMOS Mux, DIP-16)",
  "command.place.74hc74": "74HC74 (Dual D Flip-Flop CMOS, DIP-14, placeholder)",
  "command.place.74hc74.desc": "Place 74HC74 (Dual D Flip-Flop CMOS, DIP-14, placeholder)",
  "command.place.magnetic-cm-choke": "Common-Mode Choke (Würth WE-CMB, placeholder)",
  "command.place.magnetic-cm-choke.desc": "Place Common-Mode Choke (Würth WE-CMB, placeholder)",
  "command.place.adafruit-feather": "Adafruit Feather (ESP32/SAMD21, USB-C, placeholder)",
  "command.place.adafruit-feather.desc":
    "Place Adafruit Feather (ESP32/SAMD21, USB-C, placeholder)",
  "command.place.arduino-nano": "Arduino Nano (ATmega328P, USB-mini)",
  "command.place.arduino-nano.desc": "Place Arduino Nano (ATmega328P, USB-mini)",
  "command.place.arduino-uno-r3": "Arduino UNO R3 (ATmega328P, USB-B)",
  "command.place.arduino-uno-r3.desc": "Place Arduino UNO R3 (ATmega328P, USB-B)",
  "command.place.atmega2560": "ATmega2560 (Arduino Mega, TQFP-100)",
  "command.place.atmega2560.desc": "Place ATmega2560 (Arduino Mega, TQFP-100)",
  "command.place.atmega328p": "ATmega328P (Arduino UNO, DIP-28)",
  "command.place.atmega328p.desc": "Place ATmega328P (Arduino UNO, DIP-28)",
  "command.place.attiny84": "ATtiny84 (DIP-14, placeholder)",
  "command.place.attiny84.desc": "Place ATtiny84 (DIP-14, placeholder)",
  "command.place.attiny85": "ATtiny85 (DIP-8)",
  "command.place.attiny85.desc": "Place ATtiny85 (DIP-8)",
  "command.place.breakout-dip28": "DIP-28 Breakout (ATmega328P/168 bare chip, placeholder)",
  "command.place.breakout-dip28.desc":
    "Place DIP-28 Breakout (ATmega328P/168 bare chip, placeholder)",
  "command.place.breakout-dip40": "DIP-40 Breakout (ATmega2560/1284 bare chip, placeholder)",
  "command.place.breakout-dip40.desc":
    "Place DIP-40 Breakout (ATmega2560/1284 bare chip, placeholder)",
  "command.place.esp32-wroom-32": "ESP32-WROOM-32 (Module, placeholder)",
  "command.place.esp32-wroom-32.desc": "Place ESP32-WROOM-32 (Module, placeholder)",
  "command.place.esp8266": "ESP8266 (NodeMCU, placeholder)",
  "command.place.esp8266.desc": "Place ESP8266 (NodeMCU, placeholder)",
  "command.place.rp2040": "RP2040 (Raspberry Pi Pico, QFN-56, placeholder)",
  "command.place.rp2040.desc": "Place RP2040 (Raspberry Pi Pico, QFN-56, placeholder)",
  "command.place.sparkfun-pro-micro": "Sparkfun Pro Micro (ATmega32U4, USB-HID, placeholder)",
  "command.place.sparkfun-pro-micro.desc":
    "Place Sparkfun Pro Micro (ATmega32U4, USB-HID, placeholder)",
  "command.place.stm32-blue-pill": "STM32 Blue Pill (STM32F103C8, ARM Cortex-M3, placeholder)",
  "command.place.stm32-blue-pill.desc":
    "Place STM32 Blue Pill (STM32F103C8, ARM Cortex-M3, placeholder)",
  "command.place.stm32f103c8": "STM32F103C8 (Blue Pill, LQFP-48, placeholder)",
  "command.place.stm32f103c8.desc": "Place STM32F103C8 (Blue Pill, LQFP-48, placeholder)",
  "command.place.24lc256": "24LC256 (I2C EEPROM 256Kbit, DIP-8, placeholder)",
  "command.place.24lc256.desc": "Place 24LC256 (I2C EEPROM 256Kbit, DIP-8, placeholder)",
  "command.place.25lc080": "25LC080 (SPI EEPROM 8Kbit, DIP-8, placeholder)",
  "command.place.25lc080.desc": "Place 25LC080 (SPI EEPROM 8Kbit, DIP-8, placeholder)",
  "command.place.ds1307": "DS1307 (I2C RTC, DIP-8, placeholder)",
  "command.place.ds1307.desc": "Place DS1307 (I2C RTC, DIP-8, placeholder)",
  "command.place.m48t02": "M48T02 (NVRAM 16Kbit + RTC, DIP-24, placeholder)",
  "command.place.m48t02.desc": "Place M48T02 (NVRAM 16Kbit + RTC, DIP-24, placeholder)",
  "command.place.w25q128": "W25Q128 (SPI Flash 128Mbit, SOIC-8, placeholder)",
  "command.place.w25q128.desc": "Place W25Q128 (SPI Flash 128Mbit, SOIC-8, placeholder)",
  "command.place.2n7000": "2N7000 (NMOS, TO92)",
  "command.place.2n7000.desc": "Place 2N7000 (NMOS, TO92)",
  "command.place.bss138": "BSS138 (NMOS logic-level, SOT23)",
  "command.place.bss138.desc": "Place BSS138 (NMOS logic-level, SOT23)",
  "command.place.irf540": "IRF540 (NMOS power, TO220)",
  "command.place.irf540.desc": "Place IRF540 (NMOS power, TO220)",
  "command.place.irf9540": "IRF9540 (PMOS power, TO220)",
  "command.place.irf9540.desc": "Place IRF9540 (PMOS power, TO220)",
  "command.place.motor-bldc": "BLDC Motor 12V (3-phase, placeholder)",
  "command.place.motor-bldc.desc": "Place BLDC Motor 12V (3-phase, placeholder)",
  "command.place.motor-dc": "DC Motor 12V (placeholder, winding only)",
  "command.place.motor-dc.desc": "Place DC Motor 12V (placeholder, winding only)",
  "command.place.motor-servo-sg90": "Servo SG90 (PWM, 5V, placeholder)",
  "command.place.motor-servo-sg90.desc": "Place Servo SG90 (PWM, 5V, placeholder)",
  "command.place.motor-stepper-28byj48": "Stepper 28BYJ-48 (5V, 4-phase, placeholder)",
  "command.place.motor-stepper-28byj48.desc": "Place Stepper 28BYJ-48 (5V, 4-phase, placeholder)",
  "command.place.ina126": "INA126 (Precision Differential, DIP-8)",
  "command.place.ina126.desc": "Place INA126 (Precision Differential, DIP-8)",
  "command.place.lm324": "LM324 (Quad, DIP-14)",
  "command.place.lm324.desc": "Place LM324 (Quad, DIP-14)",
  "command.place.lm339": "LM339 (Quad Comparator, DIP-14)",
  "command.place.lm339.desc": "Place LM339 (Quad Comparator, DIP-14)",
  "command.place.lm358": "LM358 (General-Purpose, DIP-8)",
  "command.place.lm358.desc": "Place LM358 (General-Purpose, DIP-8)",
  "command.place.lm393": "LM393 (Comparator, DIP-8)",
  "command.place.lm393.desc": "Place LM393 (Comparator, DIP-8)",
  "command.place.ne5532": "NE5532 (Audio-Grade, DIP-8)",
  "command.place.ne5532.desc": "Place NE5532 (Audio-Grade, DIP-8)",
  "command.place.opa2134": "OPA2134 (High-End Audio, DIP-8)",
  "command.place.opa2134.desc": "Place OPA2134 (High-End Audio, DIP-8)",
  "command.place.tl072": "TL072 (FET-Input, DIP-8)",
  "command.place.tl072.desc": "Place TL072 (FET-Input, DIP-8)",
  "command.place.tl082": "TL082 (FET-Input, Low-Supply, DIP-8)",
  "command.place.tl082.desc": "Place TL082 (FET-Input, Low-Supply, DIP-8)",
  "command.place.4n25": "4N25 (Optocoupler 6-pin DIP, CTR 20%, placeholder)",
  "command.place.4n25.desc": "Place 4N25 (Optocoupler 6-pin DIP, CTR 20%, placeholder)",
  "command.place.6n137": "6N137 (High-speed Opto 10Mbit/s, DIP-8, placeholder)",
  "command.place.6n137.desc": "Place 6N137 (High-speed Opto 10Mbit/s, DIP-8, placeholder)",
  "command.place.pc817": "PC817 (Opto Sharp, DIP-4, CTR 50%, placeholder)",
  "command.place.pc817.desc": "Place PC817 (Opto Sharp, DIP-4, CTR 50%, placeholder)",
  "command.place.tlp521": "TLP521 (Quad Opto Toshiba, DIP-16, placeholder)",
  "command.place.tlp521.desc": "Place TLP521 (Quad Opto Toshiba, DIP-16, placeholder)",
  "command.place.multi-turn-pot-25": "Multi-Turn Potentiometer (25-turn, fine adjustment)",
  "command.place.multi-turn-pot-25.desc":
    "Place Multi-Turn Potentiometer (25-turn, fine adjustment)",
  "command.place.trim-pot-10t": "Trim Potentiometer (10-turn, precision)",
  "command.place.trim-pot-10t.desc": "Place Trim Potentiometer (10-turn, precision)",
  "command.place.trim-pot-1t": "Trim Potentiometer (single-turn, quick-adjust)",
  "command.place.trim-pot-1t.desc": "Place Trim Potentiometer (single-turn, quick-adjust)",
  "command.place.tip120": "TIP120 (NPN Darlington, 60V/5A, TO-220)",
  "command.place.tip120.desc": "Place TIP120 (NPN Darlington, 60V/5A, TO-220)",
  "command.place.tip125": "TIP125 (PNP Darlington, 60V/5A, TO-220)",
  "command.place.tip125.desc": "Place TIP125 (PNP Darlington, 60V/5A, TO-220)",
  "command.place.db3": "DB3 (DIAC ±32V, DO-35, placeholder)",
  "command.place.db3.desc": "Place DB3 (DIAC ±32V, DO-35, placeholder)",
  "command.place.irg4pc50ud": "IRG4PC50UD (IGBT 600V/27A, TO-247, placeholder)",
  "command.place.irg4pc50ud.desc": "Place IRG4PC50UD (IGBT 600V/27A, TO-247, placeholder)",
  "command.place.irfp240": "IRFP240 (NMOS power, 200V/20A, TO247)",
  "command.place.irfp240.desc": "Place IRFP240 (NMOS power, 200V/20A, TO247)",
  "command.place.c106": "C106M (SCR 600V/4A, TO-126, placeholder)",
  "command.place.c106.desc": "Place C106M (SCR 600V/4A, TO-126, placeholder)",
  "command.place.bta16": "BTA16-600B (TRIAC 600V/16A, TO-220, placeholder)",
  "command.place.bta16.desc": "Place BTA16-600B (TRIAC 600V/16A, TO-220, placeholder)",
  "command.place.lm4040-10v": "LM4040-10.0 (Precision Reference 10V, SOT-23)",
  "command.place.lm4040-10v.desc": "Place LM4040-10.0 (Precision Reference 10V, SOT-23)",
  "command.place.lm4040-2v5": "LM4040-2.5 (Precision Reference 2.5V, SOT-23)",
  "command.place.lm4040-2v5.desc": "Place LM4040-2.5 (Precision Reference 2.5V, SOT-23)",
  "command.place.lm4040-5v": "LM4040-5.0 (Precision Reference 5.0V, SOT-23)",
  "command.place.lm4040-5v.desc": "Place LM4040-5.0 (Precision Reference 5.0V, SOT-23)",
  "command.place.tl431": "TL431 (Adjustable Shunt Regulator, TO-92)",
  "command.place.tl431.desc": "Place TL431 (Adjustable Shunt Regulator, TO-92)",
  "command.place.zr285": "ZR285 (Low-Power Reference 2.85V, TO-92)",
  "command.place.zr285.desc": "Place ZR285 (Low-Power Reference 2.85V, TO-92)",
  "command.place.7805": "7805 (+5V Fixed, TO-220)",
  "command.place.7805.desc": "Place 7805 (+5V Fixed, TO-220)",
  "command.place.7812": "7812 (+12V Fixed, TO-220)",
  "command.place.7812.desc": "Place 7812 (+12V Fixed, TO-220)",
  "command.place.7905": "7905 (-5V Fixed, TO-220)",
  "command.place.7905.desc": "Place 7905 (-5V Fixed, TO-220)",
  "command.place.ams1117-3v3": "AMS1117-3.3 (+3.3V LDO, SOT-223)",
  "command.place.ams1117-3v3.desc": "Place AMS1117-3.3 (+3.3V LDO, SOT-223)",
  "command.place.ams1117-5v": "AMS1117-5.0 (+5V LDO, SOT-223)",
  "command.place.ams1117-5v.desc": "Place AMS1117-5.0 (+5V LDO, SOT-223)",
  "command.place.lm2596": "LM2596 (Buck Switching +5V, TO-220)",
  "command.place.lm2596.desc": "Place LM2596 (Buck Switching +5V, TO-220)",
  "command.place.lm317": "LM317 (Adjustable +5V, TO-220)",
  "command.place.lm317.desc": "Place LM317 (Adjustable +5V, TO-220)",
  "command.place.lm337": "LM337 (Adjustable -5V, TO-220)",
  "command.place.lm337.desc": "Place LM337 (Adjustable -5V, TO-220)",
  "command.place.mp1584": "MP1584 (Buck Switching +5V, SOIC-8)",
  "command.place.mp1584.desc": "Place MP1584 (Buck Switching +5V, SOIC-8)",
  "command.place.relay-dpdt-12v": "Relay DPDT 12V Coil (Omron MY2N-D2, placeholder)",
  "command.place.relay-dpdt-12v.desc": "Place Relay DPDT 12V Coil (Omron MY2N-D2, placeholder)",
  "command.place.relay-reed": "Reed Relay (Hamlin HE721A, placeholder)",
  "command.place.relay-reed.desc": "Place Reed Relay (Hamlin HE721A, placeholder)",
  "command.place.relay-spdt-5v": "Relay SPDT 5V Coil (Omron G5LE-1-5DC, placeholder)",
  "command.place.relay-spdt-5v.desc": "Place Relay SPDT 5V Coil (Omron G5LE-1-5DC, placeholder)",
  "command.place.relay-ssr": "Solid-State Relay (Omron G3MB-202P, placeholder)",
  "command.place.relay-ssr.desc": "Place Solid-State Relay (Omron G3MB-202P, placeholder)",
  "command.place.resistor-10w": "Resistor 10W (aluminum-clad)",
  "command.place.resistor-10w.desc": "Place Resistor 10W (aluminum-clad)",
  "command.place.resistor-5w": "Resistor 5W (wirewound)",
  "command.place.resistor-5w.desc": "Place Resistor 5W (wirewound)",
  "command.place.2n5109": "2N5109 (RF Amp NPN 1.2GHz, TO-39, placeholder)",
  "command.place.2n5109.desc": "Place 2N5109 (RF Amp NPN 1.2GHz, TO-39, placeholder)",
  "command.place.rf-antenna": "RF Antenna (generic, placeholder)",
  "command.place.rf-antenna.desc": "Place RF Antenna (generic, placeholder)",
  "command.place.bfg520": "BFG520 (RF NPN 9GHz, SOT-143, placeholder)",
  "command.place.bfg520.desc": "Place BFG520 (RF NPN 9GHz, SOT-143, placeholder)",
  "command.place.rf-inductor-high-q": "RF Inductor High-Q 100nH (Coilcraft-class)",
  "command.place.rf-inductor-high-q.desc": "Place RF Inductor High-Q 100nH (Coilcraft-class)",
  "command.place.rf-mixer": "RF Mixer (DBM, placeholder)",
  "command.place.rf-mixer.desc": "Place RF Mixer (DBM, placeholder)",
  "command.place.1n5817": "1N5817 (1A Schottky, DO41)",
  "command.place.1n5817.desc": "Place 1N5817 (1A Schottky, DO41)",
  "command.place.1n5819": "1N5819 (1A 40V Schottky, DO41)",
  "command.place.1n5819.desc": "Place 1N5819 (1A 40V Schottky, DO41)",
  "command.place.bc547-photo": "BC547 + LED (Homemade Phototransistor, placeholder)",
  "command.place.bc547-photo.desc": "Place BC547 + LED (Homemade Phototransistor, placeholder)",
  "command.place.bme680": "BME680 (4-in-1 Environmental Sensor, I2C, placeholder)",
  "command.place.bme680.desc": "Place BME680 (4-in-1 Environmental Sensor, I2C, placeholder)",
  "command.place.bmp180": "BMP180 (I2C Barometric, classic, placeholder)",
  "command.place.bmp180.desc": "Place BMP180 (I2C Barometric, classic, placeholder)",
  "command.place.bmp280": "BMP280 (I2C Barometric Pressure Sensor)",
  "command.place.bmp280.desc": "Place BMP280 (I2C Barometric Pressure Sensor)",
  "command.place.bpw34": "BPW34 (Si PIN Photodiode, visible+NIR, placeholder)",
  "command.place.bpw34.desc": "Place BPW34 (Si PIN Photodiode, visible+NIR, placeholder)",
  "command.place.dht11": "DHT11 (Humidity + Temperature Sensor, placeholder)",
  "command.place.dht11.desc": "Place DHT11 (Humidity + Temperature Sensor, placeholder)",
  "command.place.dht22": "DHT22 / AM2302 (Precision Humidity + Temperature, placeholder)",
  "command.place.dht22.desc":
    "Place DHT22 / AM2302 (Precision Humidity + Temperature, placeholder)",
  "command.place.ds18b20": "DS18B20 (OneWire Digital Temperature Sensor, placeholder)",
  "command.place.ds18b20.desc": "Place DS18B20 (OneWire Digital Temperature Sensor, placeholder)",
  "command.place.ds3231": "DS3231 (I2C RTC, TCXO ±2ppm)",
  "command.place.ds3231.desc": "Place DS3231 (I2C RTC, TCXO ±2ppm)",
  "command.place.hc-sr04": "HC-SR04 (Ultrasonic Distance Sensor)",
  "command.place.hc-sr04.desc": "Place HC-SR04 (Ultrasonic Distance Sensor)",
  "command.place.ldr-7mm": "Photo-Resistor 7mm (LDR, hobby)",
  "command.place.ldr-7mm.desc": "Place Photo-Resistor 7mm (LDR, hobby)",
  "command.place.ldr": "LDR (CdS Photoresistor, GL5528)",
  "command.place.ldr.desc": "Place LDR (CdS Photoresistor, GL5528)",
  "command.place.lm35": "LM35 (Linear Temperature Sensor, 10mV/°C)",
  "command.place.lm35.desc": "Place LM35 (Linear Temperature Sensor, 10mV/°C)",
  "command.place.mpu6050": "MPU6050 (6-axis IMU, I2C, placeholder)",
  "command.place.mpu6050.desc": "Place MPU6050 (6-axis IMU, I2C, placeholder)",
  "command.place.mq-2": "MQ-2 (Gas Sensor, LPG/Propane/Methane/Smoke)",
  "command.place.mq-2.desc": "Place MQ-2 (Gas Sensor, LPG/Propane/Methane/Smoke)",
  "command.place.ntc-10k": "NTC 10K (Beta=3950, temperature sensor)",
  "command.place.ntc-10k.desc": "Place NTC 10K (Beta=3950, temperature sensor)",
  "command.place.ptc": "PTC (positive temperature coefficient, protection)",
  "command.place.ptc.desc": "Place PTC (positive temperature coefficient, protection)",
  "command.place.tcrt5000": "TCRT5000 (Reflective IR Optical Sensor, placeholder)",
  "command.place.tcrt5000.desc": "Place TCRT5000 (Reflective IR Optical Sensor, placeholder)",
  "command.place.tsl2561": "TSL2561 (I2C Ambient Light Sensor)",
  "command.place.tsl2561.desc": "Place TSL2561 (I2C Ambient Light Sensor)",
  "command.place.solar-panel-5v": "Solar Panel 5V/100mA (Hobby)",
  "command.place.solar-panel-5v.desc": "Place Solar Panel 5V/100mA (Hobby)",
  "command.place.switch-dip-4": "DIP Switch 4-pos (placeholder, single channel)",
  "command.place.switch-dip-4.desc": "Place DIP Switch 4-pos (placeholder, single channel)",
  "command.place.switch-dpdt": "DPDT Toggle (6 terminal, placeholder)",
  "command.place.switch-dpdt.desc": "Place DPDT Toggle (6 terminal, placeholder)",
  "command.place.switch-push-button": "Push Button (NO Momentary)",
  "command.place.switch-push-button.desc": "Place Push Button (NO Momentary)",
  "command.place.reed-switch-magnetic": "Magnetic Reed Switch",
  "command.place.reed-switch-magnetic.desc": "Place Magnetic Reed Switch",
  "command.place.switch-spdt": "SPDT Toggle (3 terminal, placeholder)",
  "command.place.switch-spdt.desc": "Place SPDT Toggle (3 terminal, placeholder)",
  "command.place.switch-tactile": "Tactile Switch (PCB-mount, B3F-class)",
  "command.place.switch-tactile.desc": "Place Tactile Switch (PCB-mount, B3F-class)",
  "command.place.transformer-audio-1to1": "Audio Transformer 1:1 (placeholder, primary)",
  "command.place.transformer-audio-1to1.desc": "Place Audio Transformer 1:1 (placeholder, primary)",
  "command.place.transformer-center-tapped":
    "Center-Tapped Transformer 12-0-12V (placeholder, primary)",
  "command.place.transformer-center-tapped.desc":
    "Place Center-Tapped Transformer 12-0-12V (placeholder, primary)",
  "command.place.transformer-power-220-12": "Power Transformer 220V:12V (placeholder, primary)",
  "command.place.transformer-power-220-12.desc":
    "Place Power Transformer 220V:12V (placeholder, primary)",
  "command.place.1n4733": "1N4733 (5.1V Zener, DO41)",
  "command.place.1n4733.desc": "Place 1N4733 (5.1V Zener, DO41)",
  "command.place.1n4742": "1N4742 (12V Zener, DO41)",
  "command.place.1n4742.desc": "Place 1N4742 (12V Zener, DO41)",
  "command.place.1n4751": "1N4751 (30V Zener, DO41)",
  "command.place.1n4751.desc": "Place 1N4751 (30V Zener, DO41)",

  // Sprint 22 E1 — sim.* (9 commands)
  "command.sim.runDc": "Run DC (F5)",
  "command.sim.runDc.desc": "Operating point — Newton-Raphson solve",
  "command.sim.runTransient": "Run transient",
  "command.sim.runTransient.desc": "Time-domain solve — adaptive step",
  "command.sim.runAc": "Run AC (F6)",
  "command.sim.runAc.desc": "Frequency sweep — for the Bode plot",
  "command.sim.toggleLive": "Toggle live transient",
  "command.sim.toggleLive.desc": "Re-solve automatically when the circuit changes",
  "command.sim.stop": "Stop simulation",
  "command.sim.reset": "Clear results",
  "command.sim.reset.desc": "Reset DC/transient/AC outputs — circuit is preserved",
  "command.sim.configAc": "Open AC settings…",
  "command.sim.configAc.desc": "Start/stop frequency, point count, sweep distribution",
  "command.sim.configTransient": "Open transient settings…",
  // Sprint 80.Z Phase 4 D1 — command.sim.exportResults removed
  // (CSV / JSON export pipeline not wired; re-lands with the
  // export flow).

  // Sprint 22 E1 — pane.* (6 commands)
  "command.pane.landing": "Switch to welcome",
  "command.pane.simulator": "Switch to simulator",
  "command.pane.presets": "Switch to circuits",
  "command.pane.presets.desc": "Open the demo CIRCUITS catalog (Cmd+Shift+P)",
  "command.pane.inspector": "Switch to inspector",
  "command.pane.settings": "Switch to settings",
  "command.pane.shortcutsOverlay": "Open shortcut overlay",
  "command.pane.shortcutsOverlay.desc": "Show every keyboard shortcut in one panel (?)",

  // Sprint 22 E1 — preset.* (15 commands, ALL_PRESETS order)
  "command.preset.voltage-divider": "Voltage divider",
  "command.preset.voltage-divider.desc": "Two resistors + source — the canonical teaching circuit",
  "command.preset.rc-low-pass": "RC low-pass filter",
  "command.preset.rc-low-pass.desc": "First-order RC LP — fc=1/(2πRC)",
  "command.preset.rc-high-pass": "RC high-pass filter",
  "command.preset.rc-high-pass.desc": "First-order RC HP — block DC, pass AC",
  "command.preset.led-current-limiter": "LED current limiter",
  "command.preset.led-current-limiter.desc": "Series resistor + LED — the classic current-limit",
  "command.preset.rl-circuit": "RL circuit",
  "command.preset.rl-circuit.desc": "Resistor + inductor — inductive time constant τ=L/R",
  "command.preset.rlc-series-resonance": "RLC series resonance",
  "command.preset.rlc-series-resonance.desc": "Tank circuit — f₀=1/(2π√LC), Q=ωL/R",
  "command.preset.diode-clipper": "Diode clipper",
  "command.preset.diode-clipper.desc": "Two diodes — clip the sine at ±V_F",
  "command.preset.bridge-rectifier": "Bridge rectifier",
  "command.preset.bridge-rectifier.desc": "Four diodes — full-wave AC to DC",
  "command.preset.npn-common-emitter": "NPN common-emitter",
  "command.preset.npn-common-emitter.desc": "Classic bipolar amplifier — Rb/Rc/Re",
  "command.preset.opamp-non-inverting": "Op-amp non-inverting",
  "command.preset.opamp-non-inverting.desc": "Positive gain A=1+R_f/R_g",
  "command.preset.opamp-inverting": "Op-amp inverting",
  "command.preset.opamp-inverting.desc": "Negative gain A=−R_f/R_g",
  "command.preset.opamp-buffer": "Op-amp buffer (unity)",
  "command.preset.opamp-buffer.desc": "Gain=1, isolation for impedance matching",
  // Sprint 25 R2 lane 6 — missing token (S25-GAP-6 closure)
  "command.preset.opamp-astable": "Op-amp astable oscillator",
  "command.preset.opamp-astable.desc": "Schmitt-triggered square-wave generator — RC tau period",
  "command.preset.transistor-switch": "Transistor switch",
  "command.preset.transistor-switch.desc": "Drive a BJT into saturation as a relay",
  "command.preset.nmos-switch": "NMOS switch",
  "command.preset.nmos-switch.desc": "Logic-level switch — gate-controlled",
  "command.preset.zener-regulator": "Zener regulator",
  "command.preset.zener-regulator.desc": "Constant V_Z output — simple shunt regulator",

  // Sprint 80.X Sandık Kapağı (F-IA-100, ADR-0118) — i18n backfill for the 15
  // Sprint 50-2/3/4/5/6 + Sprint 54.3 presets the original ADR-0050 missed.
  // Beginner expansion (Sprint 50-2).
  "command.preset.simple-resistor-pair": "Simple resistor pair",
  "command.preset.simple-resistor-pair.desc": "Two series resistors — Ohm's law primer",
  "command.preset.parallel-resistors": "Parallel resistors",
  "command.preset.parallel-resistors.desc": "Two parallel resistors — R_eq=R₁·R₂/(R₁+R₂)",
  "command.preset.three-resistor-divider": "Three-resistor divider",
  "command.preset.three-resistor-divider.desc": "Three series resistors — staged divider topology",
  // Power expansion (Sprint 50-3).
  "command.preset.half-wave-rectifier": "Half-wave rectifier",
  "command.preset.half-wave-rectifier.desc": "Single diode — pass the positive half of AC",
  "command.preset.voltage-doubler": "Voltage doubler",
  "command.preset.voltage-doubler.desc": "Cockcroft-Walton — 2 capacitors + 2 diodes for 2× DC",
  "command.preset.rc-power-decoupling": "Power-rail decoupling capacitor",
  "command.preset.rc-power-decoupling.desc": "Parallel cap on supply — filter high-freq noise",
  // Filter + oscillator expansion (Sprint 50-4).
  "command.preset.rc-bandpass": "RC bandpass filter",
  "command.preset.rc-bandpass.desc": "HP + LP cascade — pass the f₁<f<f₂ band",
  "command.preset.wien-bridge-osc": "Wien bridge oscillator",
  "command.preset.wien-bridge-osc.desc": "Op-amp + RC bridge — low-distortion sine generation",
  "command.preset.rc-phase-shift-osc": "RC phase-shift oscillator",
  "command.preset.rc-phase-shift-osc.desc": "3-stage RC ladder — 180° phase + transistor amplifier",
  // Logic expansion (Sprint 50-5).
  "command.preset.digital-and-gate": "AND logic gate (7408)",
  "command.preset.digital-and-gate.desc": "Two-input AND — Q=A·B truth table",
  "command.preset.digital-not-gate": "NOT logic gate (7404)",
  "command.preset.digital-not-gate.desc": "Single-input inverter — Q=¬A",
  "command.preset.digital-xor-gate": "XOR logic gate (7486)",
  "command.preset.digital-xor-gate.desc": "Two-input XOR — Q=A⊕B difference detector",
  // MCU expansion (Sprint 50-6).
  "command.preset.arduino-blink-led": "Arduino blink LED",
  "command.preset.arduino-blink-led.desc": "ATmega328P + LED + 220Ω — delay-loop hello-world",
  "command.preset.arduino-button-input": "Arduino button input",
  "command.preset.arduino-button-input.desc": "Pull-up resistor + button + ATmega328P pin read",
  // Father's circuit (Sprint 54.3, VISION §10 50+ component regression).
  "command.preset.fathers-cap-bank-controller": "Father's capacitor bank controller",
  "command.preset.fathers-cap-bank-controller.desc":
    "VISION §10 father's circuit — 50+ component canonical test",

  // Sprint 22 E1 — help.* (3 commands)
  "command.help.shortcuts": "Keyboard shortcuts",
  "command.help.shortcuts.desc": "List every registered shortcut",
  "command.help.about": "About",
  "command.help.about.desc": "Version, license, studio info",
  "command.help.github": "GitHub repository",
  "command.help.github.desc": "Open the source code in a new tab",

  // Sprint 22 E1 — dev.* (5 commands, ?debug=1 only)
  "command.dev.dumpCircuit": "Dev · dump circuit to console",
  "command.dev.dumpCircuit.desc": "Stringify currentCircuit and log it as JSON",
  "command.dev.clearStorage": "Dev · clear IDB",
  "command.dev.clearStorage.desc": "Erase saved circuits and recent commands",
  "command.dev.toggleBench": "Dev · toggle perf overlay",
  "command.dev.toggleBench.desc": "Show FPS, frame time, solver ms",
  "command.dev.logRecent": "Dev · log recent commands",
  "command.dev.replayBoot": "Dev · replay boot",
  "command.dev.replayBoot.desc": "Reload the page and replay the boot sequence",

  // Sprint 25 R2 lane 1 — toast notification stack (TS-007)
  "toast.region.label": "Notifications",
  "toast.dismiss.aria": "Dismiss notification",
  "toast.kind.error": "Error",
  "toast.kind.warning": "Warning",
  "toast.kind.info": "Info",
  "toast.kind.success": "Success",

  // Sprint 25 R2 lane 2 — pre-engine validation gate (TS-008)
  "validation.toast.error.title": "Circuit error — analysis aborted",
  "validation.toast.warning.title": "Circuit warning",

  // Sprint 26 Lane 2 — F5 idempotent re-press feedback (R5-LAT-1).
  // Shown when DC analysis runs to completion. Lets the user
  // confirm that a second F5 press actually re-ran the solver
  // even when the result values are identical to the previous
  // run.
  "toast.dc.refreshed.title": "DC analysis refreshed",
  "toast.dc.refreshed.body": "Operating point recomputed",

  // Sprint 26 Lane 6 (R5-IA-8) — aria-live state-change indicators
  // for AC sweep + import commit. Mirrors the DC pattern.
  "toast.ac.refreshed.title": "AC sweep refreshed",
  "toast.ac.refreshed.body": "Bode response recomputed",
  "toast.import.success.title": "Circuit imported",
  "toast.import.success.body": "The active document was replaced (Cmd+Z to undo)",
  "validation.error.missing-ground": "No ground reference — add a GND component",
  "validation.error.dangling-pin": "Unconnected pin: {component} · {terminal}",
  "validation.error.floating-net": "Floating net: {name} (no consumers)",
  "validation.error.voltage-source-short":
    "Voltage source shorted: {component} (V+ and V- on same net)",
  "validation.warning.isolated-subgraph": "Circuit has {count} disconnected groups",

  // Sprint 53 — playback speed control (VISION §6.E, audit Item 19)
  "speed.label": "Simulation speed",
  "speed.settings.label": "Default simulation speed",
  "speed.settings.description":
    "Applied at boot. Toolbar selection changes the speed temporarily for the session.",
  "speed.bookmark.restored": "Speed restored: {speed}",

  // Sprint 56c — ERC UI integration (VISION §6.F P0)
  "erc.badge.title": "Circuit rule warnings",
  "erc.badge.zero": "Circuit clean · no rule violations",
  "erc.badge.summary.error": "{count} errors",
  "erc.badge.summary.warn": "{count} warnings",
  "erc.badge.mixed": "{errors} errors · {warns} warnings",
  "erc.panel.title": "Circuit Rules",
  "erc.panel.subtitle": "Electrical rule checker (ERC)",
  "erc.panel.empty.title": "All rules passed",
  "erc.panel.empty.body":
    "This circuit is clean at the topology + DC operating point level. The checker re-runs automatically when you edit.",
  "erc.panel.duration": "Last check {ms} ms",
  "erc.panel.dc-aware": "DC-aware rules ran",
  "erc.panel.dc-unaware": "DC-aware rules pending (run analysis)",
  "erc.panel.ignored.count": "{count} violations suppressed",
  "erc.panel.ignored.toggle.show": "Show suppressed",
  "erc.panel.ignored.toggle.hide": "Hide suppressed",
  "erc.panel.ignored.clear": "Restore all",
  "erc.panel.action.ignore": "Suppress this warning",
  "erc.panel.action.unignore": "Undo suppress",
  "erc.panel.action.locate": "Locate component",
  "erc.panel.close": "Close panel",
  "erc.panel.severity.error": "Error",
  "erc.panel.severity.warn": "Warning",
  "erc.panel.severity.info": "Info",
  "erc.rule.floating-node.title": "Floating net",
  "erc.rule.missing-ground.title": "Ground reference missing",
  "erc.rule.unconnected-pin.title": "Unconnected pin",
  "erc.rule.short-circuit.title": "Short circuit",
  "erc.rule.saturation.title": "Component in saturation",
  "erc.rule.overvoltage.title": "Overvoltage",
  "erc.rule.overheating.title": "Overheating",
  "erc.body.floating-node": "Net is connected to only one terminal — wire missing?",
  "erc.body.missing-ground": "No ground (GND) reference found.",
  "erc.body.unconnected-pin": "Component pin references a non-existent net.",
  "erc.body.short-circuit": "Direct short — two sources or a zero-Ω resistor.",
  "erc.body.saturation": "Active device pinned in saturation at the DC operating point.",
  "erc.body.overvoltage": "Node voltage exceeds the component rating.",
  "erc.body.overheating": "Component sustains T_max exceedance (Aşama 2 thermal).",
  "erc.suggestion.floating-node": "Connect this net to ground or another component.",
  "erc.suggestion.missing-ground": "Mark at least one net as ground (GND reference).",
  "erc.suggestion.unconnected-pin": "Wire this terminal to a valid net or delete the component.",
  "erc.suggestion.short-circuit": "Add a current-limiting resistor or check the source topology.",
  "erc.suggestion.saturation": "Increase the load resistor or lower the drive voltage.",
  "erc.suggestion.overvoltage": "Choose a higher-voltage-rated component or add a divider.",
  "erc.suggestion.overheating":
    "Upgrade to a higher power-rating part (e.g. 1W resistor) or reduce current.",

  // Sprint 56b — holographic formula overlay (VISION §6.G P0)
  "formula.overlay.title": "Holographic formula",
  "formula.overlay.eyebrow": "Topology recognition",
  "formula.overlay.symbolic": "Symbolic",
  "formula.overlay.numeric": "Numeric",
  "formula.overlay.close": "Close panel",
  "formula.overlay.duration": "{ms} ms",
  "formula.overlay.match-count": "{count} matches",
  "formula.overlay.no-match": "No pattern recognised.",
  "formula.voltage-divider.name": "Voltage divider",
  "formula.rc-filter.name": "RC low-pass filter",
  "formula.led-current-limit.name": "LED current limiter",
  "formula.opamp-inverting.name": "Op-amp inverting amplifier",
  "formula.opamp-non-inverting.name": "Op-amp non-inverting amplifier",
  "formula.bjt-common-emitter.name": "BJT common-emitter amplifier",

  // Sprint 57 — polish effects (VISION §6.E P1)
  "polish.tooltip.type.resistor": "Resistor",
  "polish.tooltip.type.capacitor": "Capacitor",
  "polish.tooltip.type.inductor": "Inductor",
  "polish.tooltip.type.voltage-source": "Voltage source",
  "polish.tooltip.type.ac-source": "AC source",
  "polish.tooltip.type.diode": "Diode",
  "polish.tooltip.type.led": "LED",
  "polish.tooltip.type.bjt-npn": "BJT NPN",
  "polish.tooltip.type.bjt-pnp": "BJT PNP",
  "polish.tooltip.type.opamp": "Op-amp",
  "polish.tooltip.type.ground": "Ground",
};
